import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { Landing } from "@/routes/Landing";
import { Login } from "@/routes/Login";
import { Signup } from "@/routes/Signup";
import { Onboarding } from "@/routes/Onboarding";
import { Today } from "@/routes/Today";
import { Sage } from "@/routes/Sage";
import { Settings } from "@/routes/Settings";
import { AppShell } from "@/components/AppShell";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Lazy-load the Garden route so Konva (~125 KB gzipped) is only fetched
// when the user navigates to /app/garden. Keeps the initial bundle lean.
const Garden = lazy(() =>
  import("@/routes/Garden").then((m) => ({ default: m.Garden })),
);

// Lazy-load the BedDetail route — it pulls in the Konva square-grid +
// CropSelectorSheet + plants.json on its own dependency graph.
const BedDetail = lazy(() =>
  import("@/routes/BedDetail").then((m) => ({ default: m.BedDetail })),
);

// Lazy-load Inventory + Journal (Week 2.4). Inventory carries plants.json
// (shared chunk with CropSelectorSheet) + the inventory form sheet. Journal
// carries the 8-type entry feed + dispatched form. Keeping them out of the
// initial bundle keeps Today + Garden fast on first paint.
const Inventory = lazy(() =>
  import("@/routes/Inventory").then((m) => ({ default: m.Inventory })),
);
const Journal = lazy(() =>
  import("@/routes/Journal").then((m) => ({ default: m.Journal })),
);
const Plan = lazy(() =>
  import("@/routes/Plan").then((m) => ({ default: m.Plan })),
);

function GardenFallback() {
  return (
    <div className="flex h-full items-center justify-center" data-testid="garden-route-fallback">
      <div className="h-10 w-10 animate-pulse rounded-full" style={{ background: "#EAE4DA" }} />
    </div>
  );
}

export const router = createBrowserRouter([
  { path: "/", element: <Landing /> },
  { path: "/login", element: <Login /> },
  { path: "/signup", element: <Signup /> },
  {
    path: "/onboarding",
    element: (
      <ProtectedRoute>
        <Onboarding />
      </ProtectedRoute>
    ),
  },
  {
    path: "/app",
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="today" replace /> },
      { path: "today", element: <Today /> },
      {
        path: "garden",
        element: (
          <Suspense fallback={<GardenFallback />}>
            <Garden />
          </Suspense>
        ),
      },
      {
        path: "garden/bed/:bedId",
        element: (
          <Suspense fallback={<GardenFallback />}>
            <BedDetail />
          </Suspense>
        ),
      },
      { path: "sage", element: <Sage /> },
      {
        path: "plan",
        element: (
          <Suspense fallback={<GardenFallback />}>
            <Plan />
          </Suspense>
        ),
      },
      {
        path: "journal",
        element: (
          <Suspense fallback={<GardenFallback />}>
            <Journal />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "/inventory",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<GardenFallback />}>
          <Inventory />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: "/settings",
    element: (
      <ProtectedRoute>
        <Settings />
      </ProtectedRoute>
    ),
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
